// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
// import MDBadge from "theme/components/MDBadge";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";

function AggregatorsPage() {
  const pageTitle = "Aggregators";
  usePageTitle(pageTitle);

  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [openNewCheckDialog, setOpenNewCheckDialog] = useState(false);

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [aggregators, setAggregators] = useState([]);

  const [title, setTitle] = useState(null);
  const [emailAddress, setEmailAddress] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastname] = useState(null);

  async function deleteAggregator(reference) {
    setShowLoading(true);

    await axios
      .get(`portal/aggregators/${reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function updateStatus(reference) {
    setShowLoading(true);

    await axios
      .get(`portal/aggregators/${reference}/status/update.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const tableColumns = [
    { Header: "#", accessor: "action", align: "left" },
    { Header: "Name", accessor: "name", align: "left" },
    // { Header: "Email", accessor: "email", align: "left" },
    // { Header: "Phone", accessor: "phone", align: "left" },
    { Header: "Agents", accessor: "agents", align: "left" },
    { Header: "Status", accessor: "status", align: "left" },
    { Header: "Added On", accessor: "created", align: "left" },
  ];

  const buildRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        name: row.title,
        email: row.email_address,
        phone: row.phone_number,
        agents: row.agent_count,
        created: row.created,
        status: (
          <MDBox>
            <Icon color={row.status ? "success" : "error"}>
              {row.status ? "check_circle" : "cancel"}
            </Icon>
          </MDBox>
        ),
        action: (
          <MDBox mt={1} variant="button">
            <Tooltip title="View Supervisor">
              <MDButton
                size="small"
                variant="outlined"
                color="dark"
                iconOnly
                onClick={() => {
                  navigate(`/supervisors/${row.reference}`);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                  visibility
                </Icon>
              </MDButton>
            </Tooltip>
            &nbsp;
            <Tooltip title={row.status ? "Disable Supervisor" : "Enable Supervisor"}>
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                iconOnly
                onClick={() => {
                  updateStatus(row.reference);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  {!row.status ? "play_arrow" : "pause"}
                </Icon>
              </MDButton>
            </Tooltip>
            &nbsp;
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Delete ${row.title}?`) === true) {
                  deleteAggregator(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
        // created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("portal/aggregators.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            const rows = buildRows(result.data.data.aggregators);
            setAggregators(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function createAggregator() {
    if (title === null) {
      setSeverity("error");
      setErrMsg("Name is required.");
      setOpen(true);
      return;
    }

    if (lastName === null) {
      setSeverity("error");
      setErrMsg("Contact person's last name is required.");
      setOpen(true);
      return;
    }

    if (firstName === null) {
      setSeverity("error");
      setErrMsg("Contact person's first name is required.");
      setOpen(true);
      return;
    }

    if (emailAddress === null) {
      setSeverity("error");
      setErrMsg("Email address is required.");
      setOpen(true);
      return;
    }

    if (phoneNumber === null) {
      setSeverity("error");
      setErrMsg("Phone number is required.");
      setOpen(true);
      return;
    }

    setOpenNewCheckDialog(false);
    setShowLoading(true);
    const json = {
      title: `${title}`,
      email_address: emailAddress,
      phone_number: phoneNumber,
      first_name: firstName,
      last_name: lastName,
    };

    await axios
      .post("portal/aggregators/create.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openNewCheckDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>New Supervisor</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mb={2}>
              <MDInput
                name="business_name"
                margin="dense"
                label="Business Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                name="last_name"
                margin="dense"
                label="Last Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setLastname(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                name="first_name"
                margin="dense"
                label="First Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setFirstName(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                name="title"
                margin="dense"
                label="Email Address"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setEmailAddress(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                name="title"
                margin="dense"
                label="Phone Number"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewCheckDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              createAggregator();
            }}
          >
            Add Supervisor
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mb={3}>
        {pageLoaded && (
          <MDBox>
            <MDBox>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={0}>
                  <MDBox align="center">
                    <MDBox>
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon>folder_shared</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                        Engage and manage supervisors, partner agencies and their teams effectively.
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={5} mb={3}>
                      <MDButton
                        variant="contained"
                        color="dark"
                        onClick={() => {
                          setOpenNewCheckDialog(true);
                        }}
                        startIcon={<Icon>add</Icon>}
                      >
                        Create Supervisor
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            {aggregators.length > 0 && (
              <MDBox mt={5}>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={0}>
                    <MDBox
                      pt={2}
                      px={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDTypography variant="h6" fontWeight="medium">
                        Supervisors
                      </MDTypography>
                      <MDBox>
                        <MDButton
                          variant="contained"
                          color="dark"
                          onClick={() => {
                            setOpenNewCheckDialog(true);
                          }}
                          iconOnly
                        >
                          <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDBox mt={5}>
                      <DataTable
                        table={{ columns: tableColumns, rows: aggregators }}
                        isSorted
                        entriesPerPage={false}
                        showTotalEntries
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AggregatorsPage;
